import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useFeather } from '../shell/useFeather';

interface NotificationItem {
  key: string;
  module: string;
  moduleLabel: string;
  title: string;
  recipients: string;
  description: string;
  enabled: boolean;
  recipientUsers?: NotificationUser[];
}

interface NotificationUser {
  id: number;
  name: string;
  email: string;
}

interface NotificationList {
  connected: boolean;
  masterEnabled: boolean;
  items: NotificationItem[];
  users?: NotificationUser[];
}

const MASTER_KEY = 'mattermost.all';

export function NotificationsSettings() {
  const [data, setData] = useState<NotificationList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ key: string; ids: number[] } | null>(null);

  const load = () => {
    api.get<NotificationList>('/api/v1/notifications')
      .then(setData)
      .catch(() => setError('Přehled notifikací se nepodařilo načíst.'));
  };
  useEffect(load, []);
  useFeather([data, busy]);

  const toggle = async (key: string, enabled: boolean) => {
    setBusy(key);
    setError(null);
    // Optimisticky přepneme, ať přepínač reaguje hned; při chybě se vrátí.
    setData((d) => (d == null ? d : key === MASTER_KEY
      ? { ...d, masterEnabled: enabled }
      : { ...d, items: d.items.map((i) => (i.key === key ? { ...i, enabled } : i)) }));
    try {
      await api.put('/api/v1/notifications', { key, enabled });
    } catch {
      setError('Změnu se nepodařilo uložit.');
      load();
    } finally {
      setBusy(null);
    }
  };

  const saveRecipients = async () => {
    if (!editing) return;
    setBusy(editing.key);
    setError(null);
    try {
      await api.put('/api/v1/notifications/recipients', { key: editing.key, userIds: editing.ids });
      setEditing(null);
      load();
    } catch {
      setError('Příjemce se nepodařilo uložit.');
    } finally {
      setBusy(null);
    }
  };

  if (error && !data) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!data) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;

  const groups = [...new Set(data.items.map((i) => i.moduleLabel))];
  const offCount = data.items.filter((i) => !i.enabled).length;

  return (
    <div className="notif-settings">
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="notif-status">
        <div className="notif-status-main">
          <span className={`notif-dot${data.connected ? ' je-ok' : ''}`} />
          <div>
            <strong>{data.connected ? 'Mattermost je napojený' : 'Mattermost není nastavený'}</strong>
            <small>
              {data.connected
                ? `${data.items.length} typů notifikací${offCount ? `, ${offCount} vypnuto` : ''}`
                : 'Chybí token, notifikace se neodesílají.'}
            </small>
          </div>
        </div>
        <label className="notif-switch notif-switch-master">
          <input
            type="checkbox"
            checked={data.masterEnabled}
            disabled={busy === MASTER_KEY}
            onChange={(e) => void toggle(MASTER_KEY, e.target.checked)}
          />
          <span>{data.masterEnabled ? 'Odesílání zapnuto' : 'Vše pozastaveno'}</span>
        </label>
      </div>

      {!data.masterEnabled && (
        <div className="notif-paused">
          Odesílání je pozastavené — nechodí žádná notifikace, i když je níže zapnutá.
        </div>
      )}

      {groups.map((group) => (
        <section className="notif-group" key={group}>
          <h3>{group}</h3>
          <div className="notif-card">
            {data.items.filter((i) => i.moduleLabel === group).map((item) => (
              <div className={`notif-row${item.enabled ? '' : ' je-off'}`} key={item.key}>
                <div className="notif-row-text">
                  <strong>{item.title}</strong>
                  <small>{item.description}</small>
                  {item.recipientUsers ? (
                    <span className="notif-recipients">
                      <i data-feather="user" />{' '}
                      {item.recipientUsers.length ? item.recipientUsers.map((u) => u.name).join(', ') : 'Nikdo, notifikace se neposílá'}
                      {editing?.key !== item.key && (
                        <button type="button" className="notif-recipients-edit" onClick={() => setEditing({ key: item.key, ids: (item.recipientUsers ?? []).map((u) => u.id) })}>
                          Změnit
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="notif-recipients"><i data-feather="user" /> {item.recipients}</span>
                  )}
                  {editing?.key === item.key && (
                    <div className="notif-recipients-picker">
                      {(data.users ?? []).map((u) => (
                        <label key={u.id}>
                          <input
                            type="checkbox"
                            checked={editing.ids.includes(u.id)}
                            onChange={(e) => setEditing((ed) => ed && ({ ...ed, ids: e.target.checked ? [...ed.ids, u.id] : ed.ids.filter((x) => x !== u.id) }))}
                          />
                          <span>{u.name}</span>
                          <small>{u.email}</small>
                        </label>
                      ))}
                      <div className="notif-recipients-actions">
                        <button type="button" className="btn btn-sm btn-primary" disabled={busy === item.key} onClick={() => void saveRecipients()}>Uložit příjemce</button>
                        <button type="button" className="btn btn-sm btn-link" onClick={() => setEditing(null)}>Zrušit</button>
                      </div>
                    </div>
                  )}
                </div>
                <label className="notif-switch">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    disabled={busy === item.key || !data.masterEnabled}
                    onChange={(e) => void toggle(item.key, e.target.checked)}
                  />
                  <span>{item.enabled ? 'Zapnuto' : 'Vypnuto'}</span>
                </label>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
